import React, { FC, useEffect } from 'react';
import styled from 'styled-components';

import Input from '@rover/components/src/formFields/Input';
import Elevation from '@rover/kibble/styles/Elevation';
import MQ from '@rover/kibble/styles/MediaQuery';

import useInputBodyActions from '../hooks/useInputBodyActions';
import type { SelectOption } from '../LocationInput.types';
import { getSuggestionId } from '../utils';

type StyledInputProps = {
  focused: boolean;
  isLoading?: boolean;
  hasIcon: boolean;
  floatingMenu: boolean;
  suggestionsShown: boolean;
  inputHeight?: string;
  inputFontSize?: string;
  renderIconAlignment?: 'left' | 'right';
  placeholderColor?: string;
};

const StyledInput = styled(Input)<StyledInputProps>`
  input {
    min-height: ${({ inputHeight }) => {
      return inputHeight ?? undefined;
    }};

    text-overflow: ellipsis;

    ${MQ.XS.toString()} {
      font-size: ${({ theme }) => theme.text['200'].fontSize};
    }

    padding-right: ${({ focused, isLoading, hasIcon, renderIconAlignment, theme }) => {
      if (hasIcon && renderIconAlignment === 'right') {
        return theme.space['12x'];
      }
      return focused || isLoading ? '40px' : undefined;
    }};
    padding-left: ${({ hasIcon, renderIconAlignment, theme }) =>
      hasIcon && renderIconAlignment === 'left' ? theme.space['12x'] : undefined};
    border-radius: ${({ floatingMenu, suggestionsShown, theme }) => {
      if (!floatingMenu) {
        if (suggestionsShown) {
          return `${theme.radii.primary} ${theme.radii.primary} 0 0`;
        }

        return theme.radii.primary;
      }

      return theme.radii.secondary;
    }};
    ${({ floatingMenu }) => (floatingMenu ? undefined : Elevation(2))}

    &:focus {
      border: ${({ theme }) =>
        `${theme.borderWidth.secondary} solid ${theme.colors.border.input_focus}`};
      border-bottom-width: ${({ floatingMenu, suggestionsShown, theme }) =>
        !floatingMenu && suggestionsShown ? 0 : theme.borderWidth.secondary};
    }

    &::placeholder {
      overflow: visible;
      text-overflow: ellipsis;
      color: ${({ placeholderColor }) => placeholderColor};
    }
  }
`;

type InputBodyProps = {
  inputId: string;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  inputFocused: boolean;
  inputAriaLabel?: string;
  inputFontSize?: string;
  inputHeight?: string;
  placeholder?: string;
  placeholderColor?: string;
  loading?: boolean;
  hasIcon: boolean;
  renderIconAlignment?: 'left' | 'right';
  floatingMenu: boolean;
  showSuggestions: boolean;
  disabled?: boolean;
  validationType: 'popover' | 'inline';
  validationError?: string | null | undefined;
  options?: SelectOption[];
  activeIndex?: number;
  inputValue: string;
  countryCode: string | null | undefined;
  setActiveIndex: (index: number) => void;
  handleSelectChange: (option: SelectOption) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onInputChange?: (value: string) => void;
  loadOptions?: (params: {
    query: string;
    inputId: string;
    countryCode: string | null | undefined;
  }) => void;
  setInputValue: (value: string) => void;
  setInputFocused: (focused: boolean) => void;
};

const InputBody: FC<InputBodyProps> = (props) => {
  const {
    inputId,
    inputRef,
    validationError,
    inputFocused,
    inputAriaLabel = 'search for location',
    inputHeight,
    placeholder,
    placeholderColor,
    inputFontSize,
    loading,
    hasIcon,
    renderIconAlignment,
    floatingMenu,
    showSuggestions,
    disabled,
    validationType,
    options = [],
    activeIndex,
    inputValue,
    countryCode,
    setActiveIndex,
    handleSelectChange,
    onBlur,
    onFocus,
    onInputChange,
    loadOptions,
    setInputValue,
    setInputFocused,
  } = props;
  const { handleInputChange, handleInputFocus, handleInputBlur, handleKeydown } =
    useInputBodyActions(
      inputId,
      options,
      inputValue,
      countryCode,
      setActiveIndex,
      handleSelectChange,
      setInputValue,
      setInputFocused,
      activeIndex,
      onBlur,
      onInputChange,
      loadOptions
    );

  useEffect(() => {
    if (process.env.JS_ENV_CLIENT && document.activeElement === inputRef?.current) {
      handleInputFocus();
    }
  }, []);

  return (
    <StyledInput
      id={inputId}
      inputHeight={inputHeight}
      inputFontSize={inputFontSize}
      focused={inputFocused}
      isLoading={loading}
      hasIcon={hasIcon}
      renderIconAlignment={renderIconAlignment}
      placeholder={placeholder}
      placeholderColor={placeholderColor}
      floatingMenu={!!floatingMenu}
      suggestionsShown={showSuggestions}
      data-testid="location-input"
      data-qa-id="location-input"
      autoComplete="off"
      inputRef={inputRef}
      value={inputValue}
      onChange={handleInputChange}
      disabled={disabled}
      onFocus={() => {
        handleInputFocus();
        onFocus?.();
      }}
      onBlur={handleInputBlur}
      onKeyDown={handleKeydown}
      validationType={validationType}
      validationError={validationError}
      aria-label={inputAriaLabel}
      aria-activedescendant={
        activeIndex !== undefined ? getSuggestionId(inputId, activeIndex) : undefined
      }
      aria-controls={showSuggestions ? `${inputId}-listbox` : undefined}
      aria-autocomplete="list"
    />
  );
};

export default InputBody;
